import ClearIcon from "@mui/icons-material/Clear";
import { Badge, Button, IconButton, Input, Popover, Tooltip, Typography, Dialog,  DialogContent, useMediaQuery } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PickProjectionDaysSVG from "../../../assets/svgs/PickProjectionDaysSVG.svg";
import { setProjectionDays } from "../../../store/reducers/common.slice";
import { ADD_NOTIFICATION } from "../../../store/types";
import styles from "./PickProjectionDaysPopup.module.scss";
import HtmlTooltip from "../HtmlTooltip";

const PickProjectionDaysPopup = ({
	loading,
	style,
	type = "default",
	isOpenPickProjectionDays,
	setIsOpenPickProjectionDays,	
}) => {
	const isMobile = useMediaQuery("(max-width: 600px)");
	const [open, setOpen] = useState(false);
	const { projectionDays } = useSelector((state) => state.common);
	const [value, setValue] = useState(null);
	const dispatch = useDispatch();
	const anchorRef = useRef(null);
	const initialOpenedFlag = useRef(false);
	const firstSetvalueFlag = useRef(false);

	useEffect(() => {
		if (isOpenPickProjectionDays) {
			setOpen(true);
		}
	}, [isOpenPickProjectionDays]);

	const onApplyValue = () => {
		const number = Number(value);

		if (value === null || !isNaN(number)) {
			dispatch(setProjectionDays(value === null ? null : number));
		} else {
			dispatch({
				type: ADD_NOTIFICATION,
				payload: {
					label: "Invalid number for projection days",
					type: "error",
					id: "deceasedSaveFilterError",
				},
			});
		}
		setOpen(false);
		setIsOpenPickProjectionDays && setIsOpenPickProjectionDays(false);
	};

	const onChangeValue = (value) => {
		setValue(value);

		if (!firstSetvalueFlag.current) {
			firstSetvalueFlag.current = true;
		}
	};

	useEffect(() => {
		if (!initialOpenedFlag.current) {
			initialOpenedFlag.current = true;
		}
	}, []);

	const handleClose = () => {
		setOpen(false);
		setIsOpenPickProjectionDays && setIsOpenPickProjectionDays(false);
	};

	// Content component to avoid duplication
	const PopupContent = () => (
		<div
			style={{
				width: isMobile ? "100%" : "360px",
				maxWidth: isMobile ? "400px" : "360px",
				height: isMobile ? "auto" : "214px",
				minHeight: isMobile ? "280px" : "214px",
				position: "relative",
				padding: isMobile ? "16px 16px" : "10px 13px",
				paddingBottom: isMobile ? "16px" : "15px",
				display: "flex",
				flexDirection: "column",
				justifyContent: "space-between",
				gap: isMobile ? "12px" : "0",
			}}
		>
			<div style={{ 
				display: "flex", 
				justifyContent: "space-between", 
				alignItems: "center",
				marginBottom: isMobile ? "8px" : "0"
			}}>
				<div>
					<Typography 
						variant={isMobile ? "h6" : "subtitle1"}
						sx={{ 
							fontSize: isMobile ? "16px" : "inherit",
							fontWeight: isMobile ? "600" : "inherit"
						}}
					>
						Choose a period of data:
					</Typography>
				</div>
				<div className={styles.secondSVGWrapper}>
					<PickProjectionDaysSVG width={isMobile ? 20 : 18} height={isMobile ? 20 : 18} />
				</div>
			</div>
			
			<div style={{ 
				display: "flex", 
				justifyContent: "space-between",
				flexDirection: isMobile ? "column" : "row",
				gap: isMobile ? "8px" : "4px"
			}}>
				{[
					{ label: "All", value: null },
					{ label: "30 Days", value: 30 },
					{ label: "60 Days", value: 60 },
					{ label: "90 Days", value: 90 }
				].map((option) => (
					<div key={option.value} style={{ flex: isMobile ? "1" : "auto" }}>
						<Button
							variant={
								value === option.value || (projectionDays === option.value && initialOpenedFlag.current === false)
									? "contained"
									: "outlined"
							}
							onClick={() => onChangeValue(option.value)}
							fullWidth={isMobile}
							sx={{
								height: isMobile ? "40px" : "36px",
								fontSize: isMobile ? "14px" : "14px",
								fontWeight: isMobile ? "500" : "400",
								borderRadius: isMobile ? "8px" : "4px",
								textTransform: "none",
								padding: isMobile ? "8px 12px" : "6px 16px",								
							}}
						>
							{option.label}
						</Button>
					</div>
				))}
			</div>
			
			<Typography 
				variant="subtitle1"
				sx={{ 
					textAlign: isMobile ? "center" : "left",
					margin: isMobile ? "8px 0" : "0",
					fontSize: isMobile ? "14px" : "inherit",
					fontWeight: isMobile ? "500" : "inherit"
				}}
			>
				or
			</Typography>
			
			<div style={{ 
				display: "flex", 
				height: isMobile ? "auto" : "36px",
				flexDirection: isMobile ? "column" : "row",
				gap: isMobile ? "8px" : "10px"
			}}>
				<Input
					sx={{ 
						borderRadius: isMobile ? "8px" : "5px", 
						paddingLeft: isMobile ? "12px" : "5px", 
						minWidth: isMobile ? "100%" : "60%", 
						height: isMobile ? "40px" : "100%",
						fontSize: isMobile ? "14px" : "14px",
						"& .MuiInput-input": {
							padding: isMobile ? "12px" : "8px",
						},
						"& .MuiInput-root": {
							borderRadius: isMobile ? "8px" : "5px",
						}
					}}
					placeholder="Enter custom number"
					value={value || ""}
					onChange={(e) => setValue(e.target.value)}
				/>
				<Button
					variant={firstSetvalueFlag.current === false || value === projectionDays ? "outlined" : "contained"}
					fullWidth={isMobile}
					sx={{ 
						ml: isMobile ? "0" : "10px",
						height: isMobile ? "40px" : "36px",
						fontSize: isMobile ? "14px" : "14px",
						fontWeight: isMobile ? "500" : "400",
						borderRadius: isMobile ? "8px" : "4px",
						textTransform: "none",
						padding: isMobile ? "8px 12px" : "6px 16px"						
					}}
					onClick={onApplyValue}
				>
					Apply
				</Button>
			</div>
		</div>
	);

	return (
		<>
			{/* Desktop Version - Unchanged */}
			{type === "default" && !isMobile && (
				<Tooltip title="Time Frame Converter" arrow>
					{!open && projectionDays !== null ? (
						<Badge
							color={!loading ? "error" : undefined}
							sx={{
								cursor: loading ? "default" : "pointer",
								"& .MuiBadge-badge": {
									minWidth: "12px",          // Smaller width for the badge
									height: "12px",            // Smaller height for the badge
									borderRadius: "50%",       // Make the badge round
									transform: "translate(50%, -50%) scale(1.2)",  // Position and scale down
									padding: 0,                // Remove padding for a snug fit
								}
							}}

							badgeContent={
								!loading ? (
									<HtmlTooltip content="Remove time frame converter" placement="bottom-start">
										<ClearIcon
											onClick={(e) => {
												e.stopPropagation();
												dispatch(setProjectionDays(null));
												setValue(null);
											}}
											sx={{
												fontSize: 12,
												color: "white",
											}}
										/>
									</HtmlTooltip>
								) : (
									<div className="skeleton" style={{ width: "16px", height: "16px", borderRadius: "10px" }}></div>
								)
							}
						>
							<IconButton
								aria-label="time frame converter"
								size="small"
								onClick={() => setOpen(true)}
								ref={anchorRef}
								sx={{
									width: 30,
									height: 30,
									ml: 1,
									backgroundColor: '#4879f5',
									borderRadius: "4px",
									'&:hover': {
										backgroundColor: 'rgba(0, 0, 0, 0.12)',
									},
									color: '#fff',
								}}
							>
								<PickProjectionDaysSVG width={18} height={18} />
							</IconButton>
						</Badge>
					) : (
						<IconButton
							aria-label="Compare & Highlight"
							size="small"
							onClick={() => setOpen(true)}
							ref={anchorRef}
							sx={{
								width: 30,
								height: 30,
								ml: 1,
								borderRadius: "4px",
								'&:hover': {
									backgroundColor: '#4879f5',
									color:"white",
								},
							}}
						>
							<PickProjectionDaysSVG width={18} height={18} />
						</IconButton>
					)}
				</Tooltip>
			)}

			{/* Mobile Version - New Implementation */}
			{type === "default" && isMobile && (
				<div className={styles.mobileWrapper}>
					{!open && projectionDays !== null ? (
						<Badge
							color={!loading ? "error" : undefined}
							sx={{
								cursor: loading ? "default" : "pointer",
								"& .MuiBadge-badge": {
									minWidth: "16px",
									height: "16px",
									borderRadius: "50%",
									transform: "translate(50%, -50%) scale(1.2)",
									padding: 0,
								}
							}}
							badgeContent={
								!loading ? (
									<HtmlTooltip content="Remove time frame converter" placement="bottom-start">
										<ClearIcon
											onClick={(e) => {
												e.stopPropagation();
												dispatch(setProjectionDays(null));
												setValue(null);
											}}
											sx={{
												fontSize: 14,
												color: "white",
											}}
										/>
									</HtmlTooltip>
								) : (
									<div className="skeleton" style={{ width: "20px", height: "20px", borderRadius: "10px" }}></div>
								)
							}
						>
							<Button
								variant="contained"
								onClick={() => setOpen(true)}
								ref={anchorRef}
								sx={{
									minWidth: "auto",
									width: "48px",
									height: "48px",
									borderRadius: "8px",
									backgroundColor: '#4879f5',
									color: '#fff',
									padding: 0,
									'&:hover': {
										backgroundColor: '#3d6bd4',
									},
									'&:active': {
										backgroundColor: '#2d5bc4',
									},
								}}
							>
								<PickProjectionDaysSVG width={24} height={24} />
							</Button>
						</Badge>
					) : (
						<Button
							variant="outlined"
							onClick={() => setOpen(true)}
							ref={anchorRef}
							sx={{
								minWidth: "auto",
								width: "48px",
								height: "48px",
								borderRadius: "8px",
								borderColor: '#4879f5',
								color: '#4879f5',
								padding: 0,
								'&:hover': {
									backgroundColor: '#4879f5',
									color: '#fff',
									borderColor: '#4879f5',
								},
								'&:active': {
									backgroundColor: '#3d6bd4',
									borderColor: '#3d6bd4',
								},
							}}
						>
							<PickProjectionDaysSVG width={24} height={24} />
						</Button>
					)}
				</div>
			)}

			{/* Desktop Popover */}
			{!isMobile && (
				<Popover
					id="pick-projection-days-popup"
					open={open}
					sx={{ 
						mt: "-2px", 
						ml: "-20px",
						"& .MuiPopover-paper": {
							borderRadius: "4px",
							boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
						}
					}}
					anchorEl={anchorRef.current}
					onClose={handleClose}
					anchorOrigin={{
						vertical: "top",
						horizontal: "right",
					}}
					transformOrigin={{
						vertical: "bottom",
						horizontal: "right",
					}}
				>
					<PopupContent />
				</Popover>
			)}

			{/* Mobile Dialog */}
			{isMobile && (
				<Dialog
					open={open}
					onClose={handleClose}
					maxWidth="sm"
					fullWidth
					sx={{
						"& .MuiDialog-paper": {
							borderRadius: "16px",
							margin: "16px",
							maxWidth: "calc(100vw - 32px)",
							width: "100%",
						}
					}}
				>
					<DialogContent sx={{ padding: 0 }}>
						<PopupContent />
					</DialogContent>
				</Dialog>
			)}
		</>
	);
};

export default PickProjectionDaysPopup;
